// ============================================================
// safeInternalRedirect — open-redirect-safe post-sign-in destination
// ============================================================
// When an already-signed-in visitor lands on /sign-in, Clerk may carry a
// `redirect_url` that round-tripped from a protected deep link. We must honor a
// legitimate in-app destination but NEVER navigate off-origin or to a dangerous
// scheme — an attacker who gets a victim to open
// `/sign-in?redirect_url=//evil.example` must not be bounced to evil.example.
//
// This is a PURE function (no window, no navigation, no module state) so every
// branch is unit-tested deterministically. The caller passes the current
// `origin` (window.location.origin in the browser).
//
// Accepts:
//   • a root-relative path beginning with exactly one "/"  (e.g. /blocks?x=1#y)
//   • a same-origin absolute URL                            (returns its path+query+hash)
// Rejects (→ fallback, default "/today"):
//   • protocol-relative values ("//host", "/\\host")
//   • cross-origin absolute URLs
//   • malformed URLs and non-HTTP(S) schemes (javascript:, data:, …)
//   • /sign-in and any /sign-in/... destination (would loop)
//   • missing / empty values

const DEFAULT_FALLBACK = '/today'

export function safeInternalRedirect(
  requested: string | null | undefined,
  origin: string,
  fallback: string = DEFAULT_FALLBACK
): string {
  if (!requested) return fallback

  // Protocol-relative ("//host") and backslash variants ("/\\host") are the
  // classic open-redirect vector: a URL parser resolves them to a DIFFERENT
  // origin. Reject anything whose second character is a slash or backslash.
  if (requested.length >= 2 && (requested[1] === '/' || requested[1] === '\\')) {
    return fallback
  }

  let base: URL
  try {
    base = new URL(origin)
  } catch {
    return fallback
  }

  let url: URL
  try {
    // Root-relative values resolve against origin; absolute values keep theirs.
    url = new URL(requested, base)
  } catch {
    return fallback // malformed
  }

  // Only http/https — blocks javascript:, data:, mailto:, etc.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return fallback

  // Must be same-origin (also catches whitespace-trimmed protocol-relative
  // values the character check above might miss).
  if (url.origin !== base.origin) return fallback

  // Never loop back to the sign-in surface: reject exactly /sign-in and any
  // /sign-in/... subpath. The segment boundary keeps /sign-international valid.
  if (url.pathname === '/sign-in' || url.pathname.startsWith('/sign-in/')) {
    return fallback
  }

  const dest = `${url.pathname}${url.search}${url.hash}`
  // Defensive: URL always yields a leading slash for a same-origin pathname.
  return dest.startsWith('/') ? dest : fallback
}
